import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import RouteTitle from './components/RouteTitle';

// Painel Central
import PainelLogin from './pages/PainelLogin';
import PainelCentral from './pages/PainelCentral';
import PainelEmpresas from './pages/PainelEmpresas';

// Módulo Oficina
import OficinaLogin from './pages/oficina/OficinaLogin';
import { Layout } from './components/Layout';
import Dashboard from './pages/Dashboard';
import Estoque from './pages/Estoque';
import Clientes from './pages/Clientes';
import Veiculos from './pages/Veiculos';
import Orcamentos from './pages/Orcamentos';
import Ordens from './pages/Ordens';
import Financeiro from './pages/Financeiro';
import Custos from './pages/Custos';
import Contabilidade from './pages/Contabilidade';
import Configuracoes from './pages/Configuracoes';
import Usuarios from './pages/Usuarios';
import Cargos from './pages/Cargos';
import NotasFiscais from './pages/NotasFiscais';
import Auditoria from './pages/Auditoria';

// Módulo Água
import AguaLogin from './pages/agua/AguaLogin';
import AguaAdmin from './pages/agua/AguaAdmin';
import AguaCard from './pages/agua/AguaCard';

// Módulo Financeiro MEI/ME
import FinancasLogin from './pages/financas/FinancasLogin';
import FinancasLayout from './pages/financas/FinancasLayout';
import FinancasDashboard from './pages/financas/FinancasDashboard';
import FinancasEntradas from './pages/financas/FinancasEntradas';
import FinancasSaidas from './pages/financas/FinancasSaidas';
import FinancasContas from './pages/financas/FinancasContas';
import FinancasCategorias from './pages/financas/FinancasCategorias';
import FinancasFluxoCaixa from './pages/financas/FinancasFluxoCaixa';
import FinancasRelatorios from './pages/financas/FinancasRelatorios';
import FinancasMEI from './pages/financas/FinancasMEI';
import FinancasImportar from './pages/financas/FinancasImportar';
import FinancasVendas from './pages/financas/FinancasVendas';
import FinancasClientes from './pages/financas/FinancasClientes';
import FinancasConfiguracoes from './pages/financas/FinancasConfiguracoes';

// Portal do Cliente (Oficina)
import ClientePortalLogin from './pages/oficina/cliente/ClientePortalLogin';
import ClientePortalLayout from './pages/oficina/cliente/ClientePortalLayout';
import { ClienteHome, ClienteVeiculos, ClienteServicos, ClienteOrcamentos, ClienteFinanceiro, ClienteContato, ClientePerfil } from './pages/oficina/cliente/ClientePortalPages';

// Protected Route para Oficina
const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }
  
  return token ? children : <Navigate to="/oficina/login" replace />;
};

// Protected Route para Água
const ProtectedAguaRoute = ({ children }) => {
  const token = localStorage.getItem('agua_token');
  return token ? children : <Navigate to="/agua/login" replace />;
};

// Protected Route para Painel Central
const ProtectedPainelRoute = ({ children }) => {
  const token = localStorage.getItem('painel_token');
  return token ? children : <Navigate to="/" replace />;
};

// Protected Route para Financeiro MEI/ME
const ProtectedFinancasRoute = ({ children }) => {
  const token = localStorage.getItem('financas_token');
  return token ? children : <Navigate to="/financas/login" replace />;
};

// Protected Route para Portal do Cliente (Oficina)
const ProtectedClientePortalRoute = ({ children }) => {
  const token = localStorage.getItem('cliente_portal_token');
  return token ? children : <Navigate to="/oficina/cliente" replace />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RouteTitle />
        <div className="min-h-screen bg-obsidian">
          <Toaster 
            theme="dark" 
            position="top-right"
            toastOptions={{
              style: {
                background: '#16191E',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#F8FAFC'
              }
            }}
          />
          <Routes>
            {/* Painel Central */}
            <Route path="/" element={<PainelLogin />} />
            <Route path="/painel" element={
              <ProtectedPainelRoute>
                <PainelCentral />
              </ProtectedPainelRoute>
            } />
            <Route path="/painel/empresas" element={
              <ProtectedPainelRoute>
                <PainelEmpresas />
              </ProtectedPainelRoute>
            } />

            {/* === MÓDULO OFICINA === */}
            <Route path="/oficina/login" element={<OficinaLogin />} />
            {/* Portal do Cliente — rota dedicada */}
            <Route path="/oficina/cliente" element={<ClientePortalLogin />} />
            <Route path="/oficina/cliente/home" element={<ProtectedClientePortalRoute><ClientePortalLayout /></ProtectedClientePortalRoute>}>
              <Route index element={<ClienteHome />} />
            </Route>
            <Route path="/oficina/cliente/veiculos" element={<ProtectedClientePortalRoute><ClientePortalLayout /></ProtectedClientePortalRoute>}>
              <Route index element={<ClienteVeiculos />} />
            </Route>
            <Route path="/oficina/cliente/servicos" element={<ProtectedClientePortalRoute><ClientePortalLayout /></ProtectedClientePortalRoute>}>
              <Route index element={<ClienteServicos />} />
            </Route>
            <Route path="/oficina/cliente/orcamentos" element={<ProtectedClientePortalRoute><ClientePortalLayout /></ProtectedClientePortalRoute>}>
              <Route index element={<ClienteOrcamentos />} />
            </Route>
            <Route path="/oficina/cliente/financeiro" element={<ProtectedClientePortalRoute><ClientePortalLayout /></ProtectedClientePortalRoute>}>
              <Route index element={<ClienteFinanceiro />} />
            </Route>
            <Route path="/oficina/cliente/contato" element={<ProtectedClientePortalRoute><ClientePortalLayout /></ProtectedClientePortalRoute>}>
              <Route index element={<ClienteContato />} />
            </Route>
            <Route path="/oficina/cliente/perfil" element={<ProtectedClientePortalRoute><ClientePortalLayout /></ProtectedClientePortalRoute>}>
              <Route index element={<ClientePerfil />} />
            </Route>
            <Route path="/oficina/*" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="estoque" element={<Estoque />} />
              <Route path="clientes" element={<Clientes />} />
              <Route path="veiculos" element={<Veiculos />} />
              <Route path="orcamentos" element={<Orcamentos />} />
              <Route path="ordens" element={<Ordens />} />
              <Route path="financeiro" element={<Financeiro />} />
              <Route path="custos" element={<Custos />} />
              <Route path="contabilidade" element={<Contabilidade />} />
              <Route path="configuracoes" element={<Configuracoes />} />
              <Route path="usuarios" element={<Usuarios />} />
              <Route path="cargos" element={<Cargos />} />
              <Route path="nfe" element={<NotasFiscais />} />
              <Route path="auditoria" element={<Auditoria />} />
              <Route index element={<Navigate to="dashboard" replace />} />
            </Route>

            {/* === MÓDULO ÁGUA === */}
            <Route path="/agua/login" element={<AguaLogin />} />
            <Route path="/agua/admin" element={
              <ProtectedAguaRoute>
                <AguaAdmin />
              </ProtectedAguaRoute>
            } />
            <Route path="/agua/card/:clienteId" element={<AguaCard />} />

            {/* === MÓDULO FINANCEIRO MEI/ME === */}
            <Route path="/financas/login" element={<FinancasLogin />} />
            <Route path="/financas/*" element={
              <ProtectedFinancasRoute>
                <FinancasLayout />
              </ProtectedFinancasRoute>
            }>
              <Route path="dashboard" element={<FinancasDashboard />} />
              <Route path="entradas" element={<FinancasEntradas />} />
              <Route path="saidas" element={<FinancasSaidas />} />
              <Route path="contas" element={<FinancasContas />} />
              <Route path="categorias" element={<FinancasCategorias />} />
              <Route path="fluxo-caixa" element={<FinancasFluxoCaixa />} />
              <Route path="relatorios" element={<FinancasRelatorios />} />
              <Route path="mei" element={<FinancasMEI />} />
              <Route path="importar" element={<FinancasImportar />} />
              <Route path="vendas" element={<FinancasVendas />} />
              <Route path="clientes" element={<FinancasClientes />} />
              <Route path="configuracoes" element={<FinancasConfiguracoes />} />
              <Route index element={<Navigate to="dashboard" replace />} />
            </Route>

            {/* Rotas antigas - redirecionar para oficina */}
            <Route path="/login" element={<Navigate to="/oficina/login" replace />} />
            <Route path="/dashboard" element={<Navigate to="/oficina/dashboard" replace />} />
            <Route path="/estoque" element={<Navigate to="/oficina/estoque" replace />} />
            <Route path="/clientes" element={<Navigate to="/oficina/clientes" replace />} />
            <Route path="/veiculos" element={<Navigate to="/oficina/veiculos" replace />} />
            <Route path="/orcamentos" element={<Navigate to="/oficina/orcamentos" replace />} />
            <Route path="/ordens" element={<Navigate to="/oficina/ordens" replace />} />
            <Route path="/financeiro" element={<Navigate to="/oficina/financeiro" replace />} />
            <Route path="/custos" element={<Navigate to="/oficina/custos" replace />} />
            <Route path="/contabilidade" element={<Navigate to="/oficina/contabilidade" replace />} />
            <Route path="/configuracoes" element={<Navigate to="/oficina/configuracoes" replace />} />
            <Route path="/usuarios" element={<Navigate to="/oficina/usuarios" replace />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
